import { Close } from '@carbon/icons-react';
import { Heading, Stack, Tile } from '@carbon/react';
import cx from 'classnames';
import { ReactNode, useState } from 'react';

import { getStorage } from 'hooks/useStorage';
import styles from './InfoCard.module.scss';

export type InfoCardItem = {
    icon?: ReactNode;
    title: string;
    description: string;
};

type Props = {
    headingIcon?: ReactNode;
    img?: string;
    illustration?: ReactNode;
    title: string;
    description: string;
    items?: InfoCardItem[];
    className?: string;
    isHorizontal?: boolean;
    onlyItems?: boolean;
    isClosable?: boolean;
    id?: string;
};

const InfoCard = ({
    id,
    headingIcon,
    img,
    illustration,
    title,
    description,
    items = [],
    className,
    isHorizontal = false,
    onlyItems = false,
    isClosable = false,
}: Props) => {
    const storageKey = id ? `infoCardClosed.${id}` : '';
    const [isClosed, setIsClosed] = useState(() => (
        isClosable && storageKey
            ? getStorage('local').getItem<boolean>(storageKey, false)
            : false
    ));
    const hasItems = items.length > 0;
    const hasIllustration = img || illustration;

    if (isClosed) {
        return null;
    }

    const closeCard = () => {
        if (storageKey) {
            getStorage('local').setItem(storageKey, true);
        }

        setIsClosed(true);
    };

    const renderIllustration = () => (
        <div className={styles.illustrationWrapper}>
            {img && <img className={styles.illustration} src={img} alt="" aria-hidden="true" />}
            {illustration && (
                <span className={styles.illustrationIcon} aria-hidden="true">
                    {illustration}
                </span>
            )}
        </div>
    );

    const renderHeader = () => {
        if (onlyItems) {
            return null;
        }

        return isHorizontal ? (
            <div className={styles.horizontalHeader}>
                {hasIllustration && renderIllustration()}

                <div className={styles.horizontalHeaderContent}>
                    <span className={styles.titleWrapper}>
                        {headingIcon && (
                            <span className={styles.itemIcon} aria-hidden="true">
                                {headingIcon}
                            </span>
                        )}
                        <Heading className={styles.title}>{title}</Heading>
                    </span>
                    <p className={styles.description}>{description}</p>
                </div>
            </div>
        ) : (
            <>
                {hasIllustration && renderIllustration()}

                <Stack gap={4}>
                    <span className={styles.titleWrapper}>
                        {headingIcon && (
                            <span className={styles.itemIcon} aria-hidden="true">
                                {headingIcon}
                            </span>
                        )}
                        <Heading className={styles.title}>{title}</Heading>
                    </span>
                    <p className={styles.description}>{description}</p>
                </Stack>
            </>
        );
    };

    return (
        <Tile className={cx(styles.card, isHorizontal && styles.cardHorizontal, className)}>
            {isClosable && id && (
                <button
                    type="button"
                    className={styles.closeButton}
                    aria-label="Zamknij"
                    onClick={closeCard}
                >
                    <Close size={20} />
                </button>
            )}
            <Stack gap={7}>
                {renderHeader()}

                {hasItems && (
                    <ul className={cx(styles.items, isHorizontal && styles.itemsHorizontal)}>
                        {items.map((item) => (
                            <li key={`${item.title}-${item.description}`} className={styles.item}>
                                <span className={styles.itemIcon} aria-hidden="true">
                                    {item.icon || <span className={styles.itemIconPlaceholder} />}
                                </span>

                                <div className={styles.itemContent}>
                                    <p className={styles.itemTitle}>{item.title}</p>
                                    <p className={styles.itemDescription}>{item.description}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Stack>
        </Tile>
    );
};

export default InfoCard;
